import React, { useState, useEffect, useMemo } from 'react';
import './EmailRecipientFields.css';
import { validateAddressList } from '../emailRecipients';

const OPTIONAL_FIELDS = [
  { key: 'cc', label: 'Cc' },
  { key: 'bcc', label: 'Bcc' },
  { key: 'replyTo', label: 'Reply-To' },
];

const FIELD_LABELS = { to: 'To', cc: 'Cc', bcc: 'Bcc', replyTo: 'Reply-To' };

const seedExpanded = (value) => ({
  cc: !!value?.cc,
  bcc: !!value?.bcc,
  replyTo: !!value?.replyTo,
});

/**
 * The To / Cc / Bcc / Reply-To block shown above the body on email composers.
 *
 * Fully controlled: `value` is `{ to, cc, bcc, replyTo }` of raw comma-separated strings and every
 * edit calls `onChange` with the whole object. The only local state is which optional rows are
 * open and which fields failed validation on blur.
 *
 * `prefillKey` re-seeds row visibility when it changes (a new conversation, or a new inbound
 * message arriving). Keying off that rather than off `value` means a user who collapses a
 * prefilled Cc row stays collapsed while they carry on typing.
 */
const EmailRecipientFields = ({
  value,
  onChange,
  onValidityChange,
  disabled = false,
  showTo = true,
  prefillKey = '',
}) => {
  const [expanded, setExpanded] = useState(() => seedExpanded(value));
  const [touched, setTouched] = useState({});

  useEffect(() => {
    setExpanded(seedExpanded(value));
    setTouched({});
    // Re-seeding is driven by prefillKey alone — see the note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillKey]);

  const invalidByField = useMemo(() => {
    const result = {};
    ['to', 'cc', 'bcc', 'replyTo'].forEach((key) => {
      const { invalid } = validateAddressList(value?.[key]);
      if (invalid.length) result[key] = invalid;
    });
    return result;
  }, [value]);

  const isValid = Object.keys(invalidByField).length === 0;

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  const handleFieldChange = (key) => (event) => {
    onChange?.({ ...value, [key]: event.target.value });
  };

  const toggleField = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggles = (
    <div className="email-recipient-toggles">
      {OPTIONAL_FIELDS.map(({ key, label }) => (
        <button
          key={key}
          type="button"
          className={`email-recipient-toggle${expanded[key] ? ' is-active' : ''}`}
          onClick={() => toggleField(key)}
          disabled={disabled}
        >
          {label}
        </button>
      ))}
    </div>
  );

  const renderRow = (key, trailing = null) => {
    const showError = touched[key] && invalidByField[key];
    return (
      <div className="email-recipient-row" key={key}>
        <label className="email-recipient-label" htmlFor={`email-recipient-${key}`}>
          {FIELD_LABELS[key]}
        </label>
        <div className="email-recipient-input-wrap">
          <input
            id={`email-recipient-${key}`}
            type="text"
            className={`email-recipient-input${showError ? ' is-invalid' : ''}`}
            value={value?.[key] || ''}
            onChange={handleFieldChange(key)}
            onBlur={() => setTouched((prev) => ({ ...prev, [key]: true }))}
            disabled={disabled}
            aria-label={FIELD_LABELS[key]}
          />
          {showError && (
            <div className="email-recipient-error">
              Invalid address: {invalidByField[key].join(', ')}
            </div>
          )}
        </div>
        {trailing}
      </div>
    );
  };

  return (
    <div className="email-recipient-fields">
      {/* The toggles ride along the To row, so the header stays a single line like Gmail's. */}
      {showTo
        ? renderRow('to', toggles)
        : <div className="email-recipient-row email-recipient-row--toggles">{toggles}</div>}

      {OPTIONAL_FIELDS.filter(({ key }) => expanded[key]).map(({ key }) => renderRow(key))}
    </div>
  );
};

export default EmailRecipientFields;
